import { create } from 'zustand';
import type { PopupDto, PopupInput, PopupKind } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Popups (ADR-098) — listed live first and paged on the server, written in a
 * sheet, switched on and off from the list.
 */
interface PopupsState {
  items: PopupDto[];
  nextCursor: string | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;

  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  save: (id: string | null, input: PopupInput & { name: string; kind: PopupKind }) => Promise<boolean>;
  toggle: (popup: PopupDto) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

const PAGE_SIZE = 20;
let listRequest = 0;

export const usePopupsStore = create<PopupsState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  error: null,
  saving: false,
  saveError: null,

  async load() {
    const request = ++listRequest;
    set({ loading: true, error: null });
    try {
      const page = await withSession(() => adminApi.popups.list({ limit: PAGE_SIZE }));
      if (request !== listRequest) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      if (request !== listRequest) return;
      set({ loading: false, error: errorMessage(error, 'Could not load the popups.') });
    }
  },

  async loadMore() {
    const { nextCursor, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() => adminApi.popups.list({ limit: PAGE_SIZE, cursor: nextCursor }));
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more popups.') });
    }
  },

  async save(id, input) {
    set({ saving: true, saveError: null });
    try {
      const saved = await withSession(() => (id ? adminApi.popups.update(id, input) : adminApi.popups.create(input)));
      set((state) => ({
        saving: false,
        items: id ? state.items.map((item) => (item.id === id ? saved : item)) : [saved, ...state.items],
      }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the popup.') });
      return false;
    }
  },

  async toggle(popup) {
    try {
      const saved = await withSession(() => adminApi.popups.update(popup.id, { isActive: !popup.isActive }));
      set((state) => ({ items: state.items.map((item) => (item.id === popup.id ? saved : item)) }));
    } catch (error) {
      set({ error: errorMessage(error, 'Could not switch the popup.') });
    }
  },

  async remove(id) {
    try {
      await withSession(() => adminApi.popups.remove(id));
      set((state) => ({ items: state.items.filter((item) => item.id !== id) }));
    } catch (error) {
      set({ error: errorMessage(error, 'Could not delete the popup.') });
    }
  },
}));
